import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Shield, ArrowLeft, Home, Plus, Trash2, Save, Download, Printer,
  QrCode, PawPrint, Phone, MapPin, AlertTriangle, Eye,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, borderRadius: 0, ...style }}>
      {children}
    </div>
  );
}

const inputStyle = { border: "2px solid #2D3561", borderRadius: 0 };
const selectStyle = { border: "2px solid #2D3561", borderRadius: 0 };

const SPECIES_OPTIONS = [
  { value: "perro", label: "Perro", icon: "" },
  { value: "gato", label: "Gato", icon: "" },
  { value: "ave", label: "Ave", icon: "" },
  { value: "conejo", label: "Conejo", icon: "" },
  { value: "reptil", label: "Reptil", icon: "" },
  { value: "pez", label: "Pez", icon: "" },
  { value: "roedor", label: "Roedor", icon: "" },
  { value: "otro", label: "Otro", icon: "" },
];

const emptyAnimal = { name: "", species: "", breed: "", age: "", special_needs: "", medications: "", vet_contact: "", photo_url: "" };

export default function DomicileCardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [existingCards, setExistingCards] = useState([]);

  const [formData, setFormData] = useState({
    address: "", floor: "", door: "", city: "", postal_code: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    secondary_contact_name: "", secondary_contact_phone: "",
    animals: [], special_instructions: "",
  });

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    loadExistingCards();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadExistingCards = async () => {
    try {
      const token = localStorage.getItem("ea_token");
      const res = await axios.get(`${API}/prepara/domicile-cards`, { headers: { Authorization: `Bearer ${token}` } });
      setExistingCards(res.data || []);
    } catch (error) { console.error("Error loading cards:", error); }
  };

  const generateQRCode = async (cardId) => {
    try {
      const url = `${window.location.origin}/tarjeta/${cardId}`;
      const qr = await QRCode.toDataURL(url, { width: 200, margin: 2, color: { dark: "#2D3561", light: "#ffffff" } });
      setQrCodeUrl(qr);
      return qr;
    } catch (error) { console.error("Error generating QR:", error); return null; }
  };

  const addAnimal = () => setFormData(prev => ({ ...prev, animals: [...prev.animals, { ...emptyAnimal, id: Date.now() }] }));
  const removeAnimal = (index) => setFormData(prev => ({ ...prev, animals: prev.animals.filter((_, i) => i !== index) }));
  const updateAnimal = (index, field, value) => setFormData(prev => ({ ...prev, animals: prev.animals.map((a, i) => i === index ? { ...a, [field]: value } : a) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.address || !formData.city || !formData.emergency_contact_name || !formData.emergency_contact_phone) {
      toast.error("Por favor, completa los campos obligatorios"); return;
    }
    if (formData.animals.length === 0) { toast.error("Añade al menos un animal a tu tarjeta"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("ea_token");
      const res = await axios.post(`${API}/prepara/domicile-cards`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tarjeta creada correctamente!");
      await generateQRCode(res.data.card_id);
      loadExistingCards();
    } catch (error) { toast.error("Error al guardar la tarjeta"); } finally { setSaving(false); }
  };

  const downloadAsImage = async () => {
    if (!cardRef.current) { toast.error("No hay tarjeta para descargar"); return; }
    try {
      toast.info("Generando imagen...");
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true, allowTaint: true, logging: false });
      canvas.toBlob((blob) => {
        if (!blob) { toast.error("Error al generar la imagen"); return; }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `tarjeta-emergencia-animal-${new Date().toISOString().split("T")[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
        toast.success("Imagen descargada correctamente!");
      }, "image/png", 1.0);
    } catch { toast.error("Error al descargar la imagen"); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" data-testid="domicile-card-page" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/prepara/panel")} className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
            style={{ fontFamily: B.mono, color: "#f8f7f4" }}>
            <ArrowLeft className="w-4 h-4" /> PANEL
          </button>
          <div className="h-5 w-px" style={{ background: "#f8f7f440" }} />
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #f8f7f4" }}>
              <Home className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold" style={{ color: "#f8f7f4" }}>TARJETA DE DOMICILIO</h1>
              <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>PROTEGE A TUS ANIMALES</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Intro */}
        <BCard className="p-6 mb-8" style={{ background: "#8B9A2D10", boxShadow: "4px 4px 0px #8B9A2D" }}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ background: "#8B9A2D", border: "2px solid #2D3561" }}>
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold mb-2" style={{ color: "#2D3561" }}>PARA QUÉ SIRVE ESTA TARJETA?</h2>
              <p className="text-sm leading-relaxed" style={{ color: "#666" }}>
                Si hay una emergencia y no estás en casa, los equipos de rescate verán esta tarjeta 
                en tu puerta y sabrán que hay animales que necesitan ayuda.
              </p>
            </div>
          </div>
        </BCard>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Address */}
            <BCard className="p-5" style={{ boxShadow: B.shadowSm }}>
              <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
                <div className="w-6 h-6 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #2D3561" }}>
                  <MapPin className="w-3 h-3 text-white" />
                </div>
                DIRECCIÓN DEL DOMICILIO
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DIRECCIÓN *</Label>
                  <Input placeholder="Calle, número..." value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="mt-1" style={inputStyle} data-testid="input-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>PISO</Label>
                    <Input placeholder="1º, 2º..." value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} className="mt-1" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>PUERTA</Label>
                    <Input placeholder="A, B, Izq..." value={formData.door} onChange={(e) => setFormData({ ...formData, door: e.target.value })} className="mt-1" style={inputStyle} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CIUDAD *</Label>
                    <Input placeholder="Madrid, Valencia..." value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="mt-1" style={inputStyle} data-testid="input-city" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CÓDIGO POSTAL</Label>
                    <Input placeholder="28001" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="mt-1" style={inputStyle} />
                  </div>
                </div>
              </div>
            </BCard>

            {/* Contacts */}
            <BCard className="p-5" style={{ boxShadow: B.shadowSm }}>
              <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
                <div className="w-6 h-6 flex items-center justify-center" style={{ background: "#e8637a", border: "2px solid #2D3561" }}>
                  <Phone className="w-3 h-3 text-white" />
                </div>
                CONTACTOS DE EMERGENCIA
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>NOMBRE *</Label>
                    <Input placeholder="Tu nombre o familiar" value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} className="mt-1" style={inputStyle} data-testid="input-emergency-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TELÉFONO *</Label>
                    <Input placeholder="600 123 456" value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} className="mt-1" style={inputStyle} data-testid="input-emergency-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CONTACTO SECUNDARIO</Label>
                    <Input placeholder="Vecino, familiar..." value={formData.secondary_contact_name} onChange={(e) => setFormData({ ...formData, secondary_contact_name: e.target.value })} className="mt-1" style={inputStyle} />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TELÉFONO SECUNDARIO</Label>
                    <Input placeholder="600 789 012" value={formData.secondary_contact_phone} onChange={(e) => setFormData({ ...formData, secondary_contact_phone: e.target.value })} className="mt-1" style={inputStyle} />
                  </div>
                </div>
              </div>
            </BCard>

            {/* Animals */}
            <BCard className="p-5" style={{ boxShadow: B.shadowSm }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: "#2D3561" }}>
                  <div className="w-6 h-6 flex items-center justify-center" style={{ background: "#fa9000", border: "2px solid #2D3561" }}>
                    <PawPrint className="w-3 h-3 text-white" />
                  </div>
                  MIS ANIMALES
                </h3>
                <button type="button" onClick={addAnimal} className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 hover:-translate-y-0.5 transition-all"
                  style={{ fontFamily: B.mono, color: "#8B9A2D", border: "2px solid #8B9A2D" }} data-testid="btn-add-animal">
                  <Plus className="w-3 h-3" /> AÑADIR
                </button>
              </div>
              {formData.animals.length === 0 ? (
                <div className="text-center py-8">
                  <PawPrint className="w-10 h-10 mx-auto mb-3" style={{ color: "#ccc" }} />
                  <p className="text-xs" style={{ fontFamily: B.mono, color: "#999" }}>No has añadido ningún animal todavía</p>
                  <button type="button" onClick={addAnimal} className="mt-2 text-xs font-bold uppercase tracking-wider hover:underline" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                    + AÑADIR EL PRIMERO
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence>
                    {formData.animals.map((animal, index) => (
                      <motion.div key={animal.id || index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="p-4 relative" style={{ border: "2px solid #2D3561", background: "#f8f7f4" }}>
                        <button type="button" onClick={() => removeAnimal(index)} className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center hover:bg-[#e8637a10]" style={{ border: "2px solid #e8637a" }}>
                          <Trash2 className="w-3 h-3" style={{ color: "#e8637a" }} />
                        </button>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>NOMBRE *</Label>
                            <Input placeholder="Nombre" value={animal.name} onChange={(e) => updateAnimal(index, "name", e.target.value)} className="mt-1" style={inputStyle} data-testid={`input-animal-name-${index}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>ESPECIE *</Label>
                            <Select value={animal.species} onValueChange={(v) => updateAnimal(index, "species", v)}>
                              <SelectTrigger className="mt-1" style={selectStyle} data-testid={`select-animal-species-${index}`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                              <SelectContent style={{ borderRadius: 0, border: B.border }}>
                                {SPECIES_OPTIONS.map((sp) => (<SelectItem key={sp.value} value={sp.value}><span className="flex items-center gap-2"><span>{sp.icon}</span><span>{sp.label}</span></span></SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>RAZA</Label>
                            <Input placeholder="Mestizo, Siamés..." value={animal.breed} onChange={(e) => updateAnimal(index, "breed", e.target.value)} className="mt-1" style={inputStyle} />
                          </div>
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>EDAD</Label>
                            <Input placeholder="3 años, cachorro..." value={animal.age} onChange={(e) => updateAnimal(index, "age", e.target.value)} className="mt-1" style={inputStyle} />
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>NECESIDADES ESPECIALES</Label>
                            <Textarea placeholder="Miedoso, problemas de movilidad..." value={animal.special_needs} onChange={(e) => updateAnimal(index, "special_needs", e.target.value)} className="mt-1 h-16" style={inputStyle} />
                          </div>
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>MEDICACIÓN</Label>
                            <Input placeholder="Medicamentos..." value={animal.medications} onChange={(e) => updateAnimal(index, "medications", e.target.value)} className="mt-1" style={inputStyle} />
                          </div>
                          <div>
                            <Label className="text-[10px] font-bold tracking-widest" style={{ fontFamily: B.mono }}>CONTACTO VETERINARIO</Label>
                            <Input placeholder="Nombre y teléfono" value={animal.vet_contact} onChange={(e) => updateAnimal(index, "vet_contact", e.target.value)} className="mt-1" style={inputStyle} />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </BCard>

            {/* Special Instructions */}
            <BCard className="p-5" style={{ boxShadow: B.shadowSm }}>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: "#2D3561" }}>
                <div className="w-6 h-6 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                  <AlertTriangle className="w-3 h-3 text-white" />
                </div>
                INSTRUCCIONES ESPECIALES
              </h3>
              <Textarea placeholder="Información adicional para los equipos de rescate..." value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })} className="h-24" style={inputStyle} />
            </BCard>

            {/* Submit */}
            <button type="submit" disabled={saving}
              className="w-full py-4 font-bold text-base uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ fontFamily: B.font, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadow }}
              data-testid="btn-create-card">
              {saving ? "GUARDANDO..." : <><Save className="w-5 h-5" /> CREAR TARJETA DE DOMICILIO</>}
            </button>
          </form>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-24">
            <BCard className="overflow-hidden">
              <div className="p-4" style={{ background: "#2D3561" }}>
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: "#f8f7f4" }}>
                  <Eye className="w-4 h-4" /> VISTA PREVIA
                </h3>
              </div>
              {/* Printable Card — Keep print-friendly styling */}
              <div ref={cardRef} className="bg-white p-6 print:p-8" id="domicile-card-preview">
                <div className="p-4 text-white text-center" style={{ background: "#dc2626" }}>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <AlertTriangle className="w-6 h-6" />
                    <span className="text-lg font-bold uppercase tracking-wide" style={{ fontFamily: B.font }}>
                      Atención! Animales en el interior
                    </span>
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <p className="text-sm opacity-80">En caso de emergencia, por favor rescatar</p>
                </div>
                <div className="p-4" style={{ border: "3px solid #dc2626", borderTop: 0 }}>
                  <div className="mb-4">
                    <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: "#2D3561" }}>
                      <PawPrint className="w-4 h-4" style={{ color: "#8B9A2D" }} /> Animales en este domicilio:
                    </h3>
                    {formData.animals.length === 0 ? (
                      <p className="text-sm italic" style={{ color: "#999" }}>Añade animales en el formulario</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {formData.animals.map((animal, i) => {
                          const species = SPECIES_OPTIONS.find(s => s.value === animal.species);
                          return (
                            <div key={i} className="p-2 flex items-center gap-2" style={{ background: "#f8f7f4", border: "1px solid #2D3561" }}>
                              <span className="text-xl">{species?.icon || ""}</span>
                              <div className="min-w-0">
                                <p className="text-sm font-bold truncate" style={{ color: "#2D3561" }}>{animal.name || "Sin nombre"}</p>
                                <p className="text-xs truncate" style={{ color: "#999" }}>{species?.label || "Animal"} {animal.breed && `- ${animal.breed}`}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="mb-3 pb-3" style={{ borderBottom: "2px solid #f8f7f4" }}>
                    <h3 className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: "#2D3561" }}>
                      <MapPin className="w-4 h-4" style={{ color: "#8B9A2D" }} /> Dirección:
                    </h3>
                    <p className="text-sm" style={{ color: "#666" }}>{formData.address || "—"}{formData.floor && `, ${formData.floor}`}{formData.door && ` ${formData.door}`}</p>
                    <p className="text-sm" style={{ color: "#666" }}>{formData.postal_code && `${formData.postal_code} `}{formData.city || "—"}</p>
                  </div>
                  <div className="mb-3 pb-3" style={{ borderBottom: "2px solid #f8f7f4" }}>
                    <h3 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: "#2D3561" }}>
                      <Phone className="w-4 h-4" style={{ color: "#8B9A2D" }} /> Contactos de emergencia:
                    </h3>
                    <p className="text-sm"><span className="font-bold" style={{ color: "#2D3561" }}>{formData.emergency_contact_name || "—"}:</span> <span className="font-bold" style={{ color: "#dc2626" }}>{formData.emergency_contact_phone || "—"}</span></p>
                    {formData.secondary_contact_name && (
                      <p className="text-sm"><span className="font-bold" style={{ color: "#2D3561" }}>{formData.secondary_contact_name}:</span> <span style={{ color: "#666" }}>{formData.secondary_contact_phone}</span></p>
                    )}
                  </div>
                  {formData.special_instructions && (
                    <div className="mb-3 pb-3" style={{ borderBottom: "2px solid #f8f7f4" }}>
                      <h3 className="text-sm font-bold mb-1" style={{ color: "#2D3561" }}>Instrucciones especiales:</h3>
                      <p className="text-sm" style={{ color: "#666" }}>{formData.special_instructions}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <div className="w-20 h-20 flex items-center justify-center mb-1" style={{ background: "#f8f7f4", border: "1px solid #2D3561" }}>
                        {qrCodeUrl ? <img src={qrCodeUrl} alt="QR" className="w-full h-full" /> : <QrCode className="w-10 h-10" style={{ color: "#ccc" }} />}
                      </div>
                      <p className="text-[10px]" style={{ color: "#999" }}>Escanear para más info</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center justify-end gap-1 mb-1">
                        <Shield className="w-4 h-4" style={{ color: "#8B9A2D" }} />
                        <span className="text-xs font-bold" style={{ color: "#8B9A2D" }}>Emergencia Animal</span>
                      </div>
                      <p className="text-[10px]" style={{ color: "#999" }}>emergenciaanimal.es</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Download buttons */}
              <div className="p-4 flex gap-2" style={{ background: "#f8f7f4", borderTop: "2px solid #2D3561" }}>
                <button onClick={downloadAsImage} disabled={formData.animals.length === 0}
                  className="flex-1 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:-translate-y-0.5 transition-all disabled:opacity-30"
                  style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}>
                  <Download className="w-3.5 h-3.5" /> DESCARGAR
                </button>
                <button onClick={() => window.print()} disabled={formData.animals.length === 0}
                  className="flex-1 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:-translate-y-0.5 transition-all disabled:opacity-30"
                  style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}>
                  <Printer className="w-3.5 h-3.5" /> IMPRIMIR
                </button>
              </div>
            </BCard>

            {/* Existing Cards */}
            {existingCards.length > 0 && (
              <BCard className="mt-6 p-5" style={{ boxShadow: B.shadowSm }}>
                <h3 className="text-sm font-bold mb-4" style={{ color: "#2D3561" }}>MIS TARJETAS GUARDADAS</h3>
                <div className="space-y-2">
                  {existingCards.map((card) => (
                    <div key={card.card_id} className="flex items-center justify-between p-3" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
                      <div className="flex items-center gap-3">
                        <Home className="w-5 h-5" style={{ color: "#8B9A2D" }} />
                        <div>
                          <p className="text-sm font-bold" style={{ color: "#2D3561" }}>{card.address}</p>
                          <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>{card.animals?.length || 0} ANIMALES</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {[Eye, Download].map((Icon, i) => (
                          <button key={i} className="w-7 h-7 flex items-center justify-center hover:bg-white" style={{ border: "2px solid #2D3561" }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: "#2D3561" }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </BCard>
            )}
          </div>
        </div>
      </main>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #domicile-card-preview, #domicile-card-preview * { visibility: visible; }
          #domicile-card-preview { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
